/**
 * Linear-backoff retry for transient Firestore errors, with a fast-fail when
 * the device is offline.
 *
 * # Offline: fail fast, don't pend
 *
 * The app uses an in-memory Firestore cache (lib/firebase.ts), so an offline
 * write does NOT reject — it pends indefinitely and is lost on reload, which
 * hangs the calling UI and can leave optimistic changes that never durably
 * save (e.g. a calendar event that looks moved but isn't). So when
 * `navigator.onLine` is false we throw immediately with a clear, user-facing
 * error. Behaviour then stays predictable — what you see always matches what's
 * saved, and offline failures are explicit (a sticky banner also tells the
 * user they're offline). Flaky-but-connected networks keep `navigator.onLine`
 * true and ride the retry loop below, so brief drops / latency still recover.
 *
 * # What this does NOT solve
 *
 * Earlier versions retried `permission-denied` aggressively to work
 * around the auth↔Firestore propagation race after signup. That race
 * was real but is now eliminated upstream by routing all post-signup
 * writes through the `ensure_user_profile` callable (see
 * `auth.ts` and `web/functions/user_provisioning.py`). A permission-denied
 * here now means the rule actually denied the request, and retrying
 * just delays the inevitable error. So we treat permission-denied as
 * fatal and surface it immediately.
 *
 * # What this still does
 *
 * Retries genuinely transient codes:
 *   - `unavailable`        — Firestore backend hiccup, brief outage
 *   - `resource-exhausted` — quota / rate-limit, usually self-clears
 */

import { auth } from './firebase';
import { log } from './logger';

interface FirestoreError {
  code?: string;
  name?: string;
  message?: string;
}

const RETRYABLE_CODES = new Set(['unavailable', 'resource-exhausted']);

/**
 * Thrown when a write is attempted while the device reports offline. The
 * message is user-facing — forms surface `error.message` directly.
 */
export class OfflineError extends Error {
  constructor() {
    super("You're offline — reconnect to save your changes.");
    this.name = 'OfflineError';
  }
}

export async function firestoreWithRetry<T>(
  operation: () => Promise<T>,
  opName: string,
  maxRetries = 3,
  baseDelayMs = 500,
): Promise<T> {
  const maxAttempts = maxRetries + 1;
  const startMs = performance.now();

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Re-checked each attempt so a mid-retry disconnect also fails fast rather
    // than pending on the next write.
    if (globalThis.navigator?.onLine === false) {
      throw new OfflineError();
    }
    try {
      return await operation();
    } catch (raw) {
      const error = raw as FirestoreError;
      const retryable = error.code !== undefined && RETRYABLE_CODES.has(error.code);

      if (!retryable || attempt === maxAttempts - 1) {
        log.error('Firestore op exhausted retries', {
          opName,
          attempts: attempt + 1,
          errorCode: error.code,
          errorMessage: error.message,
          totalElapsedMs: Math.round(performance.now() - startMs),
          authUid: auth.currentUser?.uid ?? null,
          online: globalThis.navigator?.onLine ?? true,
        });
        throw raw;
      }

      log.info(
        'Firestore retry',
        { opName, attempt: attempt + 1, errorCode: error.code, errorMessage: error.message },
        { sendToCloud: true },
      );
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (attempt + 1)));
    }
  }
  throw new Error('firestoreWithRetry: unreachable');
}
